import { Platform } from 'react-native';
import Constants from 'expo-constants';
import {
  getApiUrl,
  getDirectApiUrl,
  getYoutubeUploadChunkApiBase,
  getYoutubeUploadJsonApiBase,
} from './apiConfig';

/** Bump when upload strategy changes — grep production logs for this string. */
export const YOUTUBE_UPLOAD_METHOD_VERSION = '2026-07-04-web-mobile-fileinput-v9';

export const YOUTUBE_UPLOAD_METHODS = {
  NATIVE_RESUMABLE: 'native-resumable-v3',
  WEB_MOBILE_XHR: 'web-mobile-xhr-v6',
  WEB_MOBILE_RESUMABLE: 'web-mobile-resumable-v4',
  WEB_MOBILE_SESSION_BINARY: 'web-mobile-session-binary-v4',
  WEB_MOBILE_MATERIALIZED: 'web-mobile-materialized-v5',
  WEB_SESSION_B64: 'web-session-b64-v2',
  WEB_RESUMABLE_FETCH: 'web-resumable-fetch-v1',
};

/** Mobile browser (Android/iOS Chrome/Safari) — not the native app. */
export function isMobileWeb() {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

const LOG_PREFIX = '[YT Upload]';

function safeUriHint(uri) {
  if (!uri) return null;
  const s = String(uri);
  if (s.length <= 48) return s;
  return `${s.slice(0, 24)}…${s.slice(-12)}`;
}

export function getUploadEnvironmentSnapshot() {
  const extra = Constants?.expoConfig?.extra || {};
  return {
    methodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
    platform: Platform.OS,
    isDev: typeof __DEV__ !== 'undefined' ? __DEV__ : null,
    nodeEnv: process.env.NODE_ENV || null,
    apiUrl: getApiUrl() || '(same-origin /api)',
    directApiUrl: getDirectApiUrl() || '(none)',
    jsonApiBase: getYoutubeUploadJsonApiBase() || '(none)',
    chunkApiBase: getYoutubeUploadChunkApiBase() || '(none)',
    appApiUrl: extra.apiUrl || null,
    localApiUrl: extra.localApiUrl || null,
    userAgent:
      Platform.OS === 'web' && typeof navigator !== 'undefined'
        ? navigator.userAgent
        : null,
  };
}

export function logYoutubeUpload(event, details = {}) {
  const payload = {
    ts: new Date().toISOString(),
    event,
    methodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
    ...details,
  };
  console.log(LOG_PREFIX, event, payload);
}

export function logYoutubeUploadPhase(method, phase, details = {}) {
  logYoutubeUpload('phase', {
    method,
    phase,
    ...details,
  });
}

export function logYoutubeUploadError(method, phase, error, details = {}) {
  const err = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
  console.error(LOG_PREFIX, 'error', {
    ts: new Date().toISOString(),
    methodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
    method,
    phase,
    errorName: err.name,
    errorMessage: err.message,
    isNetworkError: /network|failed to fetch|load failed/i.test(err.message),
    ...summarizeError(err),
    ...details,
  });
}

function summarizeError(err) {
  return {
    stack: err.stack ? String(err.stack).split('\n').slice(0, 6).join('\n') : null,
  };
}

export function logYoutubeUploadStart(method, details = {}) {
  logYoutubeUpload('start', {
    method,
    env: getUploadEnvironmentSnapshot(),
    ...details,
  });
}

export function logYoutubeUploadSuccess(method, details = {}) {
  logYoutubeUpload('success', {
    method,
    ...details,
  });
}

export function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return String(n);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export { safeUriHint, LOG_PREFIX };
